// src/lib/domains/admin.ts
// CANON v1.0.8 Single-Domain Policy: ONLY supabank.xyz

import { getSurfaceOrigins } from '@supabank/config/domain';

const ADMIN_DOMAIN_SUFFIXES = ['.supabank.xyz'];

function matchesAdminSuffix(hostname: string): boolean {
  return ADMIN_DOMAIN_SUFFIXES.some((suffix) => hostname === `admin${suffix}` || (hostname.endsWith(suffix) && hostname.startsWith('admin.')));
}

function resolveCanonicalAdminOrigin(currentHostname: string): string | null {
  const origins = getSurfaceOrigins('admin');
  if (!origins.length) return null;

  const lowerHost = currentHostname.toLowerCase();

  const pickOrigin = (matcher: (host: string) => boolean) => {
    for (const origin of origins) {
      try {
        const url = new URL(origin);
        if (matcher(url.hostname.toLowerCase())) {
          return origin;
        }
      } catch {
        // ignore invalid origin definitions
      }
    }
    return null;
  };

  if (lowerHost.includes('staging')) {
    const staging = pickOrigin((host) => host.includes('staging'));
    if (staging) return staging;
  }

  if (lowerHost.includes('localhost') || lowerHost.includes('127.0.0.1')) {
    const local = pickOrigin((host) => host.includes('localhost') || host.includes('127.0.0.1'));
    if (local) return local;
  }

  if (lowerHost.includes('supabank.xyz')) {
    const supabank = pickOrigin((host) => host.includes('supabank.xyz'));
    if (supabank) return supabank;
  }

  return origins[0] ?? null;
}

function maybeRedirectToCanonicalAdminSurface(hostname: string, pathname: string) {
  if (typeof window === 'undefined') return;
  if (!pathname.startsWith('/admin')) return;

  const normalizedHost = hostname.toLowerCase();
  if (matchesAdminSuffix(normalizedHost)) return;

  const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1';
  if (isLocalHost) return;

  const targetOrigin = resolveCanonicalAdminOrigin(normalizedHost);
  if (!targetOrigin) return;

  const currentOrigin = window.location.origin.replace(/\/$/, '');
  const normalizedTarget = targetOrigin.replace(/\/$/, '');
  if (currentOrigin === normalizedTarget) return;

  try {
    const canonical = new URL(targetOrigin);
    const nextUrl = new URL(window.location.href);
    nextUrl.protocol = canonical.protocol;
    nextUrl.host = canonical.host;
    console.info('[AdminDomain] Redirecting to canonical admin surface', {
      from: window.location.origin,
      to: canonical.origin,
    });
    window.location.replace(nextUrl.toString());
  } catch (error) {
    console.warn('[AdminDomain] Failed to redirect to canonical admin surface', error);
  }
}

export function isAdminHostname(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const normalized = hostname.trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === 'admin') return true;
  return matchesAdminSuffix(normalized);
}

export function isAdminDomain(): boolean {
  if (typeof window === 'undefined') return false;
  
  // E2E Testing Bypass - treat localhost as admin domain when bypass is active
  if (window.localStorage.getItem('e2e_admin_bypass') === 'true') {
    console.log('[E2E Bypass] isAdminDomain treating localhost as admin domain');
    return true;
  }

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  maybeRedirectToCanonicalAdminSurface(hostname, pathname);

  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    pathname.startsWith('/admin')
  ) {
    console.log('[AdminDomain] treating localhost path as admin surface', { hostname, pathname });
    return true;
  }
  
  return isAdminHostname(hostname);
}
